import moment from "moment";
import { NUTRITION_FIELDS } from "../components/smart/DietDiary/DietDiary.constants";
import { DEFAULT_MOMENT_LOCALE, isRtl } from "../i18n/helpers";
import {
  dailyTargetIds,
  DateFormat,
  getMacronutrientColor,
  kcalMultiplier,
  UNIT,
} from "../modules/constants";
import { sortObjectByKeys } from "../modules/utils";

export const decimalPlaces = 1;

export const UNKNOWN_NUTRIENT_VALUE = "-";

export const emptyTotals = {
  calories: "0.00kcal",
  totalFats: "0.00 g",
  longChainFats: "0.00 g",
  mediumChainFats: "0.00 g",
};

const DEFAULT_MACRONUTRIENT_OBJ = {
  value: 0.0,
  percentage: 0.0,
  unit: UNIT.DEFAULT,
};

export const emptyMealStudyDrug =
  '{"aboutMeal":[{}],"items":[{"title":"Study Drug","hideMeal":true}],"type":"study_drug"}';

export const roundMealValue = (n) =>
  Math.round(parseFloat(n) * Math.pow(10, decimalPlaces)) /
  Math.pow(10, decimalPlaces);

export const getRoundedValue = (source) => {
  if (source === "") {
    source = 0;
  }
  return Number(roundMealValue(source ?? 0)).toFixed(decimalPlaces);
};

export const calculateMealPercentages = (meal) => {
  const totalValues = meal.calories.value;

  meal.protein.percentage = 0;
  meal.carbohydrates.percentage = 0;
  meal.mediumChainFats.percentage = 0;
  meal.longChainFats.percentage = 0;

  if (totalValues > 0) {
    meal.protein.percentage = (meal.protein.kcal / totalValues) * 100;
    meal.carbohydrates.percentage =
      (meal.carbohydrates.kcal / totalValues) * 100;
    meal.mediumChainFats.percentage =
      (meal.mediumChainFats.kcal / totalValues) * 100;
    meal.longChainFats.percentage =
      (meal.longChainFats.kcal / totalValues) * 100;
  }

  return meal;
};

export const isBetweenDates = (week, date) => {
  return moment(date).isBetween(
    moment(week[0]).startOf("day"),
    moment(week[1]).endOf("day"),
    null,
    "[]"
  ); //inclusive
};

//Generate weeks given a date range
export const generateWeeks = (startDay, endDay) => {
  const weeks = [];
  const startDate = moment(startDay);
  const endDate = moment(endDay);

  while (startDate.isBefore(endDate)) {
    let startWeek = startDate.clone().startOf("day");
    let endDateWeek = startWeek.clone().endOf("day").add(6, "days");

    startDate.add(7, "days");
    weeks.push([
      startWeek.format(DateFormat.INVERTED_DATE),
      endDateWeek.format(DateFormat.INVERTED_DATE),
    ]);
  }
  return weeks;
};

export const calculateKcal = (item) => {
  return {
    ...item,
    carbohydrates: {
      ...item.carbohydrates,
      kcal: (item.carbohydrates?.value || 0) * kcalMultiplier.carbs,
    },
    mediumChainFats: {
      ...item.mediumChainFats,
      kcal: (item.mediumChainFats?.value || 0) * kcalMultiplier.mcf,
    },
    longChainFats: {
      ...item.longChainFats,
      kcal: (item.longChainFats?.value || 0) * kcalMultiplier.lcf,
    },
    protein: {
      ...item.protein,
      kcal: (item.protein?.value || 0) * kcalMultiplier.protein,
    },
  };
};

export const calculateKcalAndPercentage = (meal) => {
  const kcal = calculateKcal(meal);
  const percentages = calculateMealPercentages(kcal);
  return {
    ...meal,
    ...kcal,
    ...percentages,
  };
};

export const getFinalKcalAndPercentage = (finalData) => {
  return finalData.map((item) => {
    //Per day
    const kcalperDay = calculateKcal(item);
    const percentagesPerDay = calculateMealPercentages(kcalperDay);

    //for each meal
    const mealsKcal = item.meals.map((meal) => {
      return calculateKcalAndPercentage(meal);
    });
    return { ...item, ...kcalperDay, ...percentagesPerDay, meals: mealsKcal };
  });
};

export const calcKcalByAttrId = (value, id) => {
  switch (id) {
    case dailyTargetIds.CARBS_ATTR_ID:
      return value * kcalMultiplier.carbs;
    case dailyTargetIds.PROTEIN_ATTR_ID:
      return value * kcalMultiplier.protein;
    case dailyTargetIds.LONG_CHAIN_FATS_ATTR_ID:
      return value * kcalMultiplier.lcf;
    case dailyTargetIds.MEDIUM_CHAIN_FATS_ATTR_ID:
      return value * kcalMultiplier.mcf;
    default:
      return value;
  }
};

export const addOtherSlice = (data, theme, t) => {
  const otherfrq = 100 - data.reduce((acc, item) => acc + item?.frequency, 0);

  return {
    letter: t("appText.other"),
    frequency: otherfrq,
    color: getMacronutrientColor("other", theme),
    percentage: "",
    info: "",
    subInfo: "",
    isVisible: false,
  };
};

export const areTotalsEmpty = (totals) => {
  if (
    totals.calories === emptyTotals.calories &&
    totals.longChainFats === emptyTotals.longChainFats &&
    totals.mediumChainFats === emptyTotals.mediumChainFats &&
    totals.totalFats === emptyTotals.totalFats
  ) {
    return true;
  }

  return false;
};

export const getMealEntriesString = (meal) => {
  const itemTitles = meal?.items?.map((item) => {
    return item.title;
  });

  return itemTitles?.filter((title) => title !== null).join(", ");
};

export const getMealDescription = (items) => {
  const itemTitles = items?.map((item, index) => {
    return index < 2 ? item.title : null;
  });

  let description = itemTitles?.filter((title) => title !== null).join(", ");

  if (items?.length > 2) {
    description += "...";
  }

  return description;
};

export const aggregateMeals = (array = []) => {
  const tempData = array.reduce(
    (p, c) => {
      return {
        calories: {
          percentage: p.calories.percentage + c.calories.percentage,
          value: p.calories.value + c.calories.value,
          unit: c.calories.unit,
        },
        mediumChainFats: {
          percentage:
            p.mediumChainFats.percentage + c.mediumChainFats.percentage,
          value: p.mediumChainFats.value + c.mediumChainFats.value,
          unit: c.mediumChainFats.unit,
        },
        longChainFats: {
          percentage: p.longChainFats.percentage + c.longChainFats.percentage,
          value: p.longChainFats.value + c.longChainFats.value,
          unit: c.longChainFats.unit,
        },
        protein: {
          percentage: p.protein.percentage + c.protein.percentage,
          value: p.protein.value + c.protein.value,
          unit: c.protein.unit,
        },
        carbohydrates: {
          percentage: p.carbohydrates.percentage + c.carbohydrates.percentage,
          value: p.carbohydrates.value + c.carbohydrates.value,
          unit: c.carbohydrates.unit,
        },
      };
    },
    {
      calories: {
        percentage: 0,
        value: 0,
        unit: "",
      },
      mediumChainFats: {
        percentage: 0,
        value: 0,
        unit: "",
      },
      longChainFats: {
        percentage: 0,
        value: 0,
        unit: "",
      },
      protein: {
        percentage: 0,
        value: 0,
        unit: "",
      },
      carbohydrates: {
        percentage: 0,
        value: 0,
        unit: "",
      },
    }
  );

  return tempData;
};

export const aggregateMealsByDay = (
  dailyEntries,
  dateFormat = "dddd DD/MMM/YYYY"
) => {
  const formattedData = [];

  dailyEntries?.forEach((dailyEntry) => {
    const { date, completed, fastingDay, missedSchool, sickDay } = dailyEntry;

    const dayObj = {
      date: moment(date).format(dateFormat),
      dateDefaultLocale: moment(date)
        .locale(DEFAULT_MOMENT_LOCALE)
        .format(dateFormat),
      completed: completed,
      fastingDay: fastingDay,
      missedSchool: missedSchool,
      sickDay: sickDay,
      meals: [],
    };

    dailyEntry.meals?.forEach((meal) => {
      const {
        mealJson,
        mealDate,
        mealId,
        reason,
        reasonForChange,
        administration,
        feedingType,
        endDate,
        paperRecapture,
        recall,
        hasPendingChangeRequest,
      } = meal;

      const items = JSON.parse(mealJson);

      const mealObj = {
        mealId: mealId,
        time: moment(mealDate).format("H:mm A"),
        description: getMealDescription(items.items),
        items: items.items,
        calories: { ...items.aboutMeal.Calories },
        protein: items.aboutMeal.Protein
          ? { ...items.aboutMeal.Protein }
          : DEFAULT_MACRONUTRIENT_OBJ,
        carbohydrates: items.aboutMeal.Carbs
          ? { ...items.aboutMeal.Carbs }
          : DEFAULT_MACRONUTRIENT_OBJ,
        mediumChainFats: items.aboutMeal.MediumChainFats
          ? { ...items.aboutMeal.MediumChainFats }
          : DEFAULT_MACRONUTRIENT_OBJ,
        longChainFats: items.aboutMeal.LongChainFats
          ? { ...items.aboutMeal.LongChainFats }
          : DEFAULT_MACRONUTRIENT_OBJ,
        json: mealJson,
        dateTime: mealDate,
        reason: reason,
        reasonForChange: reasonForChange,
        administeredBy: administration,
        feedingType: feedingType,
        endDate: endDate,
        paperRecapture: paperRecapture,
        is24hRecall: recall,
        hasPendingChangeRequest,
      };

      dayObj.meals.push(mealObj);
    });

    const mealDaySum = aggregateMeals(dayObj.meals);
    dayObj.calories = mealDaySum.calories;
    dayObj.protein = mealDaySum.protein;
    dayObj.carbohydrates = mealDaySum.carbohydrates;
    dayObj.mediumChainFats = mealDaySum.mediumChainFats;
    dayObj.longChainFats = mealDaySum.longChainFats;

    formattedData.push(dayObj);
  });

  formattedData.sort((elemPrev, elemNext) => {
    return moment(elemNext.dateDefaultLocale).diff(
      moment(elemPrev.dateDefaultLocale)
    );
  });

  return formattedData;
};

export const roundMealPercentage = (value) => {
  return Math.min(value, 100);
};

export const generateAboutMeal = (nutritionalInfo, totalCalories) => {
  const tempAboutMeal = {};

  NUTRITION_FIELDS.forEach((nutritionField) => {
    const nutrient = nutritionalInfo.find(
      ({ attr_id }) => attr_id === nutritionField.attr_id
    );

    if (nutrient && nutrient.value !== UNKNOWN_NUTRIENT_VALUE) {
      const numericValue = nutrient.numericValue;
      const aboutMealLetter = nutrient.aboutMealLetter || "";

      const frequency = roundMealValue(
        (calcKcalByAttrId(numericValue, nutritionField.attr_id) /
          totalCalories) *
          100.0
      );

      tempAboutMeal[aboutMealLetter] = {
        value: numericValue,
        percentage: frequency,
        unit: nutrient.unit || "",
      };
    }
  });

  return sortObjectByKeys(tempAboutMeal);
};

export const getAverageGoals = (data) => {
  if (!data || data.length === 0) {
    return {
      calories: {
        percentage: 0,
        value: 0,
        unit: "kcal",
      },
      longChainFats: {
        percentage: 0,
        value: 0,
        unit: "g",
      },
      mediumChainFats: {
        percentage: 0,
        value: 0,
        unit: "g",
      },
      protein: {
        percentage: 0,
        value: 0,
        unit: "g",
      },
      carbohydrates: {
        percentage: 0,
        value: 0,
        unit: "g",
      },
    };
  }

  const tempData = data.reduce((p, c) => {
    return {
      calories: {
        percentage: p.calories.percentage + c.calories.percentage,
        value: p.calories.value + c.calories.value,
        unit: c.calories.unit,
        kcal: p.calories.kcal + c.calories.kcal,
      },
      longChainFats: {
        percentage: p.longChainFats.percentage + c.longChainFats.percentage,
        value: p.longChainFats.value + c.longChainFats.value,
        unit: c.longChainFats.unit,
        kcal: p.longChainFats.kcal + c.longChainFats.kcal,
      },
      mediumChainFats: {
        percentage: p.mediumChainFats.percentage + c.mediumChainFats.percentage,
        value: p.mediumChainFats.value + c.mediumChainFats.value,
        unit: c.mediumChainFats.unit,
        kcal: p.mediumChainFats.kcal + c.mediumChainFats.kcal,
      },
      protein: {
        percentage: p.protein.percentage + c.protein.percentage,
        value: p.protein.value + c.protein.value,
        unit: c.protein.unit,
        kcal: p.protein.kcal + c.protein.kcal,
      },
      carbohydrates: {
        percentage: p.carbohydrates.percentage + c.carbohydrates.percentage,
        value: p.carbohydrates.value + c.carbohydrates.value,
        unit: c.carbohydrates.unit,
        kcal: p.carbohydrates.kcal + c.carbohydrates.kcal,
      },
    };
  });

  tempData.calories.percentage = tempData.calories.percentage / data.length;
  tempData.protein.percentage = tempData.protein.percentage / data.length;
  tempData.longChainFats.percentage =
    tempData.longChainFats.percentage / data.length;
  tempData.mediumChainFats.percentage =
    tempData.mediumChainFats.percentage / data.length;
  tempData.carbohydrates.percentage =
    tempData.carbohydrates.percentage / data.length;

  tempData.calories.value = tempData.calories.value / data.length;
  tempData.longChainFats.value = tempData.longChainFats.value / data.length;
  tempData.mediumChainFats.value = tempData.mediumChainFats.value / data.length;
  tempData.protein.value = tempData.protein.value / data.length;
  tempData.carbohydrates.value = tempData.carbohydrates.value / data.length;

  return tempData;
};

export const countNutrientsByAttrId = (attr_id, items) => {
  let found = false;
  let total = 0;
  items?.map(({ fullNutrients, qty, defaultMeasure, selectedMeasure }) => {
    fullNutrients?.map((nutrient) => {
      if (nutrient.attr_id === attr_id) {
        found = true;
        total +=
          (((nutrient.value / (defaultMeasure.serving_weight ?? 1)) *
            (selectedMeasure.serving_weight ?? 1)) /
            selectedMeasure.qty) *
          qty;
      }
    });
  });
  return found ? total : 0;
};
